// Visual Flow Builder React Flow Canvas (PRD §1.9, WP-08).
// Interactive drag-and-drop workspace with minimap, controls, grid background, and custom node renderers.
import React, { useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
  type Node,
  type Edge,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomFlowNode from './CustomFlowNode';
import type { FlowNodeType } from '../../lib/flows';

const nodeTypes = {
  customNode: CustomFlowNode,
};

interface FlowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  onNodeSelect: (nodeId: string | null) => void;
  onAddNodeAtPosition: (type: FlowNodeType, position: { x: number; y: number }) => void;
  className?: string;
}

export function FlowCanvasContent({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeSelect,
  onAddNodeAtPosition,
  className = '',
}: FlowCanvasProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('application/reactflow-type') as FlowNodeType;
      if (!nodeType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      onAddNodeAtPosition(nodeType, position);
    },
    [screenToFlowPosition, onAddNodeAtPosition]
  );

  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      onNodeSelect(node.id);
    },
    [onNodeSelect]
  );

  const handlePaneClick = useCallback(() => {
    onNodeSelect(null);
  }, [onNodeSelect]);

  return (
    <div
      ref={reactFlowWrapper}
      className={`relative h-full w-full bg-slate-50 dark:bg-slate-950 ${className}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={2}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#0284c7', strokeWidth: 2 },
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={18}
          size={1.5}
          className="opacity-60 dark:opacity-30"
        />
        <Controls
          className="!border-line !bg-surface !shadow-sm dark:!border-slate-800 dark:!bg-slate-900"
          showInteractive={false}
        />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="!border-line !bg-surface/80 !shadow-sm dark:!border-slate-800 dark:!bg-slate-900/80"
          nodeColor={(n) => {
            const data = n.data as any;
            const t = data?.nodeType;
            if (t === 'condition') return '#8b5cf6';
            if (t === 'interactive_message' || t === 'cta_url_button') return '#06b6d4';
            if (t === 'wait_input' || t === 'ai_agent_reply') return '#f59e0b';
            if (t === 'subscribe_sequence' || t === 'label_assign') return '#ec4899';
            return '#0284c7';
          }}
        />
      </ReactFlow>
    </div>
  );
}

export default FlowCanvasContent;
